import { useEffect, useRef, useState } from "react";
import type { IncidentActionResult } from "@/types";
import {
  advanceExecutionSteps,
  completeExecutionSteps,
  initialExecutionSteps,
  type ExecutionStep,
} from "@/lib/incidentExecution";

interface UseIncidentExecutionOptions {
  onAction: (incidentId: string, actionId: string) => Promise<IncidentActionResult>;
  onReload: () => Promise<{ resolved: boolean }>;
}

export interface ExecutionOutcome {
  success: boolean;
  resolved: boolean;
  passed: boolean;
  actionId: string;
  result: IncidentActionResult;
  recoveryMs: number;
}

export function useIncidentExecution({ onAction, onReload }: UseIncidentExecutionOptions) {
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [lastActionId, setLastActionId] = useState<string | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const reset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    setRunning(false);
    setSteps([]);
    setLastActionId(null);
  };

  const execute = async (incidentId: string, actionId: string): Promise<ExecutionOutcome> => {
    reset();
    setRunning(true);
    setLastActionId(actionId);
    const startedAt = Date.now();
    let currentSteps = initialExecutionSteps(actionId);
    setSteps(currentSteps);

    tickRef.current = setInterval(() => {
      setSteps((prev) => {
        const next = advanceExecutionSteps(prev);
        currentSteps = next;
        return next;
      });
    }, 2200);

    try {
      const result = await onAction(incidentId, actionId);
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;

      const reloaded = await onReload();
      const verdict = result.verification?.verdict;
      const passed =
        reloaded.resolved ||
        result.verificationPassed === true ||
        verdict === "healthy";
      const recoveryMs = Date.now() - startedAt;

      setSteps(completeExecutionSteps(currentSteps, result.success && passed));

      return {
        success: result.success,
        resolved: reloaded.resolved,
        passed,
        actionId,
        result,
        recoveryMs,
      };
    } catch (err) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setSteps(completeExecutionSteps(currentSteps, false));
      throw err;
    } finally {
      setRunning(false);
    }
  };

  return {
    running,
    steps,
    lastActionId,
    execute,
    reset,
  };
}
